import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '@/store/store';
import {
  fetchTeamMembers,
  loadMoreTeamMembers,
  setSearchText as setTeamSearchText,
} from '@/store/teamSlice';
import {
  fetchInvitations,
  setSearchText as setInvitationsSearchText,
  resetShouldRefetch,
} from '@/store/invitationsSlice';
import AppLayout from '@/components/layout/AppLayout';
import Navbar from '@/components/ui/navbar';
import { TeamHeaderIcon } from '@/components/ui/SvgIcon';
import { Button } from '@/components/ui/button';
import Typography from '@/components/ui/typography';
import { InvitePlusIcon, InviteUserIcon } from '@/components/ui/SvgIcon';
import UserCard from '@/components/team/userCard';
import InvitedUserCard from '@/components/team/invitedUserCard';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import InviteModal from '@/components/team/InviteModal';
import EditUserModal from '@/components/team/EditUserModal';
import DeleteConfirmationModal from '@/components/team/DeleteConfirmationModal';
import SearchInput from '@/components/ui/SearchInput';
import InfiniteScrollList from '@/components/ui/InfiniteScrollList';
import { useDebounce } from '@/hooks';
import { usePermissions } from '@/hooks/usePermissions';
import { ehrUsernameLabel } from '@/utils/ehr';
import { TeamMember } from '@/types/api';

const Team: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { hasPermission } = usePermissions();

  // Team members state
  const { members, loading, loadingMore, error, pagination, searchText } =
    useSelector((state: RootState) => state.team);

  // Invitations state
  const {
    invitations,
    loading: invitationsLoading,
    error: invitationsError,
    searchText: invitationsSearchText,
    shouldRefetch,
  } = useSelector((state: RootState) => state.invitations);

  const { selectedHospital } = useSelector(
    (state: RootState) => state.hospitals,
  );
  const { user: currentUser } = useSelector((state: RootState) => state.user);
  const currentUserEmail = currentUser?.email?.trim().toLowerCase();
  const ehrLabel = ehrUsernameLabel(selectedHospital?.ehr);

  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<TeamMember | null>(null);
  const [activeTab, setActiveTab] = useState('team');

  // Permission checks
  const canInviteUsers = hasPermission('write-user');
  const canEditUsers = hasPermission('write-user');
  const canDeleteUsers = hasPermission('delete-user');

  // Debounce search text to avoid too many API calls
  const debouncedSearchText = useDebounce(searchText, 500);
  const debouncedInvitationsSearchText = useDebounce(
    invitationsSearchText,
    500,
  );

  // Fetch team members on component mount and when search changes
  useEffect(() => {
    if (selectedHospital?.tenantId) {
      dispatch(
        fetchTeamMembers({
          pageNo: 0,
          pageSize: pagination.pageSize,
          searchText: debouncedSearchText,
          tenantId: selectedHospital.tenantId,
        }),
      );
    }
  }, [dispatch, selectedHospital?.tenantId, debouncedSearchText]);

  // Fetch invitations when tab changes to invited or when search changes
  useEffect(() => {
    if (selectedHospital?.tenantId && activeTab === 'invited') {
      dispatch(
        fetchInvitations({
          searchText: debouncedInvitationsSearchText,
          tenantId: selectedHospital.tenantId,
        }),
      );
    }
  }, [
    dispatch,
    selectedHospital?.tenantId,
    debouncedInvitationsSearchText,
    activeTab,
  ]);

  useEffect(() => {
    if (
      shouldRefetch &&
      selectedHospital?.tenantId &&
      activeTab === 'invited'
    ) {
      dispatch(
        fetchInvitations({
          searchText: debouncedInvitationsSearchText,
          tenantId: selectedHospital.tenantId,
        }),
      );
      dispatch(resetShouldRefetch());
    }
  }, [
    shouldRefetch,
    selectedHospital?.tenantId,
    activeTab,
    dispatch,
    debouncedInvitationsSearchText,
  ]);

  // Handle load more for team members infinite scroll
  const handleLoadMoreTeamMembers = useCallback(() => {
    if (!loadingMore && !pagination.isLast) {
      const nextPage = pagination.currentPage + 1;
      dispatch(
        loadMoreTeamMembers({
          pageNo: nextPage,
          pageSize: pagination.pageSize,
          searchText: debouncedSearchText,
          tenantId: selectedHospital?.tenantId,
        }),
      );
    }
  }, [
    dispatch,
    loadingMore,
    pagination.isLast,
    pagination.currentPage,
    pagination.pageSize,
    debouncedSearchText,
    selectedHospital?.tenantId,
  ]);

  const handleTeamSearch = (value: string) => {
    dispatch(setTeamSearchText(value));
  };

  const handleInvitationsSearch = (value: string) => {
    dispatch(setInvitationsSearchText(value));
  };

  const handleTabChange = (value: string) => {
    setActiveTab(value);
  };

  const handleInvite = () => {
    setIsInviteModalOpen(true);
  };

  const handleEditUser = (user: TeamMember) => {
    if (canEditUsers) {
      setSelectedUser(user);
      setIsEditModalOpen(true);
    }
  };

  const handleDeleteUser = (user: TeamMember) => {
    if (canDeleteUsers) {
      setSelectedUser(user);
      setIsDeleteModalOpen(true);
    }
  };

  // Empty state components
  const teamEmptyState = (
    <div className="w-full border border-gray-200 rounded-lg p-15 text-center shadow-sm bg-white">
      <div className="flex flex-col items-center">
        <img
          src={InviteUserIcon}
          alt="User"
          className="h-10 w-10 text-gray-400 mb-4"
        />
        <h3 className="text-lg font-semibold text-gray-900 mb-1">
          {searchText ? 'No team members found' : 'No team members found'}
        </h3>
        <p className="text-sm text-gray-600">
          {searchText
            ? 'Try adjusting your search terms or invite new team members.'
            : 'Invite new team members to get started.'}
        </p>
      </div>
    </div>
  );

  const invitationsEmptyState = (
    <div className="w-full border border-gray-200 rounded-lg p-15 text-center shadow-sm bg-white">
      <div className="flex flex-col items-center">
        <img
          src={InviteUserIcon}
          alt="User"
          className="h-10 w-10 text-gray-400 mb-4"
        />
        <h3 className="text-lg font-semibold text-gray-900 mb-1">
          {invitationsSearchText
            ? 'No invited members found'
            : 'No invited members found'}
        </h3>
        <p className="text-sm text-gray-600">
          {invitationsSearchText
            ? 'Try adjusting your search terms or invite new team members.'
            : 'Invite new team members to get started.'}
        </p>
      </div>
    </div>
  );

  return (
    <AppLayout currentPage="Team">
      <Navbar
        title="Team"
        showSearch={false}
        showAvatar={true}
        showWelcomeMessage={false}
        icon={
          <img src={TeamHeaderIcon} alt="Team Header" className="h-10 w-10" />
        }
      />

      <div className="space-y-6 p-6">
        <Tabs
          defaultValue="team"
          className="w-full"
          onValueChange={handleTabChange}
        >
          <TabsList className="grid w-full sm:w-2/3 md:w-1/2 lg:w-1/3 xl:w-1/4 grid-cols-2 h-10">
            <TabsTrigger
              value="team"
              className="hover:cursor-pointer h-[38px] transition-transform duration-150 data-[state=inactive]:hover:scale-95 data-[state=inactive]:focus-visible:scale-95 data-[state=inactive]:hover:px-3 data-[state=active]:hover:bg-transparent"
            >
              Active Members
            </TabsTrigger>
            <TabsTrigger
              value="invited"
              className="hover:cursor-pointer h-[38px] transition-transform duration-150 data-[state=inactive]:hover:scale-95 data-[state=inactive]:focus-visible:scale-95 data-[state=inactive]:hover:px-3 data-[state=active]:hover:bg-transparent"
            >
              Invited Members
            </TabsTrigger>
          </TabsList>

          <div className="mt-6">
            <TabsContent value="team">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <SearchInput
                  value={searchText}
                  onChange={handleTeamSearch}
                  showLoadingIndicator={searchText !== debouncedSearchText}
                />

                {/* Only show invite button if user has write-user permission */}
                {canInviteUsers && (
                  <div className="w-full sm:w-auto">
                    <Button
                      variant="outline"
                      onClick={handleInvite}
                      className="flex items-center space-x-2 w-full sm:w-auto"
                    >
                      <img
                        src={InvitePlusIcon}
                        alt="Invite Plus"
                        className="h-4 w-4"
                      />
                      <Typography
                        variant="h3"
                        color="neutral1000"
                        weight="normal"
                      >
                        Invite
                      </Typography>
                    </Button>
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="invited">
              <div className="flex items-center">
                <SearchInput
                  value={invitationsSearchText}
                  onChange={handleInvitationsSearch}
                  showLoadingIndicator={
                    invitationsSearchText !== debouncedInvitationsSearchText
                  }
                />
              </div>
            </TabsContent>
          </div>

          <TabsContent value="team" className="mt-6">
            <div
              className="scrollbar-hide"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              <InfiniteScrollList
                loading={loading}
                loadingMore={loadingMore}
                error={error}
                hasMore={!pagination.isLast}
                onLoadMore={handleLoadMoreTeamMembers}
                emptyStateComponent={
                  members.length === 0 ? teamEmptyState : undefined
                }
                className="max-h-[calc(100vh-300px)] overflow-y-auto scrollbar-hide [&_*]:scrollbar-hide"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 2xl:grid-cols-3 gap-4 sm:gap-5 md:gap-6">
                  {members
                    .filter(
                      (member) =>
                        member.email?.trim().toLowerCase() !== currentUserEmail,
                    )
                    .map((user) => (
                      <UserCard
                        key={user.uuid}
                        user={user}
                        ehrUsernameLabel={ehrLabel}
                        onEdit={canEditUsers ? handleEditUser : undefined}
                        onDelete={canDeleteUsers ? handleDeleteUser : undefined}
                      />
                    ))}
                </div>
              </InfiniteScrollList>
            </div>
          </TabsContent>

          <TabsContent value="invited" className="mt-6">
            <div
              className="scrollbar-hide"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              <InfiniteScrollList
                loading={invitationsLoading}
                loadingMore={false}
                error={invitationsError}
                hasMore={false}
                onLoadMore={() => {}}
                emptyStateComponent={
                  invitations.length === 0 ? invitationsEmptyState : undefined
                }
                className="max-h-[calc(100vh-300px)] overflow-y-auto scrollbar-hide [&_*]:scrollbar-hide"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 2xl:grid-cols-3 gap-4 sm:gap-5 md:gap-6">
                  {invitations
                    .filter(
                      (user) =>
                        user.email?.trim().toLowerCase() !== currentUserEmail,
                    )
                    .map((user) => (
                      <InvitedUserCard
                        key={user.uuid}
                        user={user}
                        tenantId={selectedHospital?.tenantId || ''}
                      />
                    ))}
                </div>
              </InfiniteScrollList>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Only render modals if user has appropriate permissions */}
      <>
        {canInviteUsers && (
          <InviteModal
            open={isInviteModalOpen}
            onOpenChange={setIsInviteModalOpen}
            ehrUsernameLabel={ehrLabel}
          />
        )}

        {canEditUsers && (
          <EditUserModal
            open={isEditModalOpen}
            onOpenChange={setIsEditModalOpen}
            user={selectedUser}
            tenantId={selectedHospital?.tenantId || ''}
            ehrUsernameLabel={ehrLabel}
          />
        )}

        {canDeleteUsers && (
          <DeleteConfirmationModal
            open={isDeleteModalOpen}
            onOpenChange={setIsDeleteModalOpen}
            userName={
              selectedUser
                ? `${selectedUser.firstName} ${selectedUser.lastName}`
                : ''
            }
            userUuid={selectedUser?.uuid || ''}
            tenantId={selectedHospital?.tenantId || ''}
          />
        )}
      </>
    </AppLayout>
  );
};

export default Team;
